/**
 * The portfolio view of a house-level sheet: one row per plat, each opening
 * that sheet's rows for its houses in place, as the Rent & expenses plats do.
 * A click on the row opens the plat itself; the ▸ opens it here.
 */

import { ChevronRight } from "lucide-react";
import React from "react";

export interface PlatGroup {
	platId: string;
	platName: string;
	houses: number;
	/** One line about the plat on this sheet. */
	summary: React.ReactNode;
}

export interface PlatGroupsProps {
	groups: readonly PlatGroup[];
	expanded: ReadonlySet<string>;
	onToggle: (platId: string) => void;
	onOpen: (platId: string) => void;
	render: (platId: string) => React.ReactNode;
}

const PlatGroups: React.FC<PlatGroupsProps> = ({
	groups,
	expanded,
	onToggle,
	onOpen,
	render,
}) => {
	return (
		<div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
			<div className="border-b border-gray-200 bg-[#fafafa] px-3 py-2">
				<span className="text-[12.5px] font-semibold text-[#1F2937]">Plat</span>
			</div>
			{groups.map((group) => {
				const open = expanded.has(group.platId);
				return (
					<div key={group.platId} className="border-b border-[#f1f2f4] last:border-b-0">
						{/* The row opens the plat; the chevron, its own button, opens it here. */}
						<div
							onClick={() => onOpen(group.platId)}
							className="flex cursor-pointer items-start gap-1.5 px-3 py-2.5 hover:bg-[#f6fbfb]"
						>
							<button
								type="button"
								onClick={(event) => {
									event.stopPropagation();
									onToggle(group.platId);
								}}
								aria-expanded={open}
								aria-label={`${open ? "Hide" : "Show"} ${group.platName}'s houses here`}
								className="-ml-1 mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded text-[#5B6472] hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782]"
							>
								<ChevronRight className={`h-4 w-4 transition-transform ${open ? "rotate-90" : ""}`} aria-hidden />
							</button>
							<div className="min-w-0 flex-1">
								<button
									type="button"
									onClick={(event) => {
										event.stopPropagation();
										onOpen(group.platId);
									}}
									className="text-left text-[13px] font-semibold text-[#004C4B] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782]"
								>
									{group.platName}
								</button>
								<div className="text-xs text-[#6B7280]">
									{group.houses} houses
									{group.summary ? <> · {group.summary}</> : null}
								</div>
							</div>
						</div>
						{open && <div className="bg-[#f7f8f8] px-3 pb-3 pt-1">{render(group.platId)}</div>}
					</div>
				);
			})}
		</div>
	);
};

export default PlatGroups;
